import { SIGNUP_SCENARIO } from '@src/const/localStorageConsts';
import { SignupScenario } from '@src/const/signupScenarios';
import { useAuthStore } from '@stores/authStore';
import { AuthUserDto, Country, GERMANY, Role } from '@therame/shared';
import { Dayjs } from 'dayjs';
import { defineStore } from 'pinia';
import { ref } from 'vue';

const initFromLocalStorage = localStorage.getItem(SIGNUP_SCENARIO);
if (!initFromLocalStorage) {
  localStorage.setItem(SIGNUP_SCENARIO, SignupScenario.PATIENT_NEW);
}

export const useSignupStore = defineStore('signup', () => {
  const authStore = useAuthStore();

  const scenario = ref<SignupScenario>(
    initFromLocalStorage !== null && (SignupScenario as any)[initFromLocalStorage] !== undefined
      ? (SignupScenario as any)[initFromLocalStorage]
      : SignupScenario.ADMIN_NEW,
  );
  const step = ref<number>(0);
  const email = ref<string>('');
  const person = ref<object | null>(null);
  const salutation = ref<string | null>(null);
  const firstName = ref<string>('');
  const lastName = ref<string>('');
  const birthdate = ref<Dayjs | null>(null);
  const telephone = ref<string>('');
  const therapy = ref<string | null>(null);
  const agreedNewsletter = ref<boolean>(false);
  const officeName = ref<string>('');
  const officeStreet = ref<string>('');
  const officeZipcode = ref<string>('');
  const officeTown = ref<string>('');
  const officeCountry = ref<Country>(GERMANY);
  const officeSubdivisionCode = ref<string | null>(null);

  const usesSeparateInvoiceAddress = ref<boolean>(false);
  const officeCompanyNameForInvoice = ref<string>('');
  const officeStreetForInvoice = ref<string>('');
  const officeZipcodeForInvoice = ref<string>('');
  const officeTownForInvoice = ref<string>('');
  const officeCountryForInvoice = ref<Country>(GERMANY);

  const agreedCorrectness = ref<boolean>(false);
  const insurance = ref<string>('');

  const chooseSignupScenario = (user?: AuthUserDto) => {
    if (user?.role === Role.PATIENT && !user.officeId) {
      setScenario(SignupScenario.PATIENT_NEW);
      step.value = 2;
    } else if (user?.role === Role.PATIENT) {
      setScenario(SignupScenario.PATIENT_ONBOARD);
      step.value = 1;
    } else if (user?.role === Role.THERAPIST) {
      setScenario(SignupScenario.THERAPIST);
      step.value = 1;
    } else if (user?.role === Role.MANAGEMENT) {
      setScenario(SignupScenario.MANAGEMENT);
      step.value = 1;
    } else if (user?.role === Role.ADMIN && !!user?.officeId) {
      setScenario(SignupScenario.ADMIN_ADDITIONAL);
      step.value = 1;
    } else if (user?.role === Role.PREVENTION) {
      setScenario(SignupScenario.PREVENTION);
      step.value = 0;
    } else {
      // An office signup has no Person until „Praxis eröffnen“ (#1833); what
      // is left of its scenario is the page asking to verify the address.
      setScenario(SignupScenario.ADMIN_NEW);
      step.value = 1;
    }
  };

  const loadDataFromToken = () => {
    firstName.value = authStore.authContext?.user?.firstName || '';
    lastName.value = authStore.authContext?.user?.lastName || '';
    email.value = authStore.authContext?.user?.email || '';
  };

  const getScenario = () => {
    return scenario.value;
  };

  const setScenario = (scen: SignupScenario) => {
    scenario.value = scen;
    localStorage.setItem(SIGNUP_SCENARIO, scenario.value);
  };

  return {
    getScenario,
    setScenario,
    step,
    email,
    person,
    salutation,
    firstName,
    lastName,
    birthdate,
    telephone,
    therapy,
    agreedNewsletter,
    officeName,
    officeStreet,
    officeZipcode,
    officeTown,
    officeCountry,
    officeSubdivisionCode,
    usesSeparateInvoiceAddress,
    officeCompanyNameForInvoice,
    officeStreetForInvoice,
    officeZipcodeForInvoice,
    officeTownForInvoice,
    officeCountryForInvoice,
    agreedCorrectness,
    insurance,
    chooseSignupScenario,
    loadDataFromToken,
  };
});
